import React from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import {
  CheckCircle, AlertTriangle, Loader2, Workflow, ArrowRight, Sparkles
} from 'lucide-react';

export function SmartCopyDialog({
  open, onOpenChange, draft, suggestions, selectedFlowToCopy,
  setSelectedFlowToCopy, loadingSuggestions, approving, onApprove
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Approve Draft Part - Smart Copy
          </DialogTitle>
          <DialogDescription>
            Create part and optionally pre-fill a process flow from a similar existing part.
          </DialogDescription>
        </DialogHeader>
        
        {draft && (
          <div className="space-y-4">
            {selectedFlowToCopy && (
              <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                <AlertTriangle className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-blue-800">
                  <strong>Form-Fill Mode:</strong> The selected process flow will be loaded into the Process Flow Builder for your review. 
                  You must click <strong>"Save"</strong> in the builder to commit the flow.
                </div>
              </div>
            )}
            
            <Card className="bg-muted/50">
              <CardContent className="p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Description:</span>
                    <p className="font-medium">{draft.description || '-'}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Type:</span>
                    <p className="font-medium">{draft.part_type || '-'}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Grade:</span>
                    <p className="font-medium">{draft.grade || '-'}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Customer P/N:</span>
                    <p className="font-medium font-mono">{draft.customer_part_no || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Workflow className="h-4 w-4" />
                Similar Process Flows (Optional - Select to Pre-Fill)
              </Label>
              
              {loadingSuggestions ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  <span className="text-sm text-muted-foreground">Finding similar parts...</span>
                </div>
              ) : suggestions.length === 0 ? (
                <div className="text-center py-6 border rounded-lg bg-muted/30">
                  <Workflow className="h-8 w-8 mx-auto mb-2 text-muted-foreground opacity-50" />
                  <p className="text-sm text-muted-foreground">No similar process flows found</p>
                  <p className="text-xs text-muted-foreground">You can create a process flow after the part is approved</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[250px] overflow-y-auto">
                  {suggestions.map((suggestion, idx) => (
                    <div
                      key={suggestion.process_flow_id || idx}
                      className={`border rounded-lg p-3 cursor-pointer transition-all ${
                        selectedFlowToCopy === suggestion.process_flow_id
                          ? 'border-primary bg-primary/5 ring-1 ring-primary'
                          : 'hover:border-muted-foreground/50 hover:bg-muted/30'
                      }`}
                      onClick={() => setSelectedFlowToCopy(
                        selectedFlowToCopy === suggestion.process_flow_id ? null : suggestion.process_flow_id
                      )}
                      data-testid={`suggestion-${idx}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-medium text-primary">
                              {suggestion.source_part_no}
                            </span>
                            <Badge variant="outline" className="text-xs">
                              {Math.round(suggestion.similarity_score * 100)}% match
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                            {suggestion.source_description}
                          </p>
                        </div>
                        {selectedFlowToCopy === suggestion.process_flow_id && (
                          <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                        )}
                      </div>
                      
                      {suggestion.process_flow_details && (
                        <div className="mt-2 pt-2 border-t">
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Workflow className="h-3 w-3" />
                            <span>Process Flow:</span>
                          </div>
                          <div className="flex items-center gap-1 mt-1 flex-wrap">
                            {suggestion.process_flow_details.operations.map((op, opIdx) => (
                              <React.Fragment key={`op-${op}-${opIdx}`}>
                                <Badge variant="secondary" className="text-xs">
                                  {op}
                                </Badge>
                                {opIdx < suggestion.process_flow_details.operations.length - 1 && (
                                  <ArrowRight className="h-3 w-3 text-muted-foreground" />
                                )}
                              </React.Fragment>
                            ))}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {suggestion.process_flow_details.operation_count} operations • 
                            {suggestion.process_flow_details.total_cycle_time ? 
                              ` ${suggestion.process_flow_details.total_cycle_time}s cycle time` : 
                              ' No cycle time set'}
                          </p>
                        </div>
                      )}
                      
                      {suggestion.matching_keywords?.length > 0 && (
                        <div className="mt-2 flex items-center gap-1 flex-wrap">
                          <span className="text-xs text-muted-foreground">Matching:</span>
                          {suggestion.matching_keywords.slice(0, 5).map((kw) => (
                            <Badge key={`kw-${kw}`} variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                              {kw}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={approving}>
            Cancel
          </Button>
          <Button
            onClick={() => onApprove(draft?.id, selectedFlowToCopy)}
            disabled={approving || !draft}
            data-testid="confirm-smart-copy-approve"
          >
            {approving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating Part...
              </>
            ) : selectedFlowToCopy ? (
              <>
                <ArrowRight className="h-4 w-4 mr-2" />
                Create Part & Open Flow Builder
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Create Part (No Flow)
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
